// ** React Imports
import { useState, useEffect, useRef } from "react";

// ** Third Party Components
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiperNew/react";
import { Virtual } from "swiperNew/modules";
import HoverVideoPlayer from "components/video/HoverVideoPlayer";

// ** Custom Components
import Block from "components/layout/Block";
import Paragraph from "components/typography/Paragraph";
import NavigationArrowThumbnails from "containers/Modal/components/Swipers/NavigationArrowThumbnails";
import StyledIcon from "components/global/StyledIcon/StyledIcon";
import { ReactComponent as PlaySolidIcon } from "resources/icons/solid/play-solid.svg";
import { StyledOverlay } from "styles/components/Overlay.styled";

// ** Custom Hooks
import useBreakpoint from "hooks/useBreakpoint";
import useMobileDetect from "hooks/useMobileDetectHook";

// ** Utils & Helpers
import api from "helpers/ApiClient";

// Import Swiper styles
import "swiperNew/css";

// #####################################################

const THUMBNAIL_SIZE = 90;
const SPACE_BETWEEN = 3;

// #####################################################

const Wrapper = styled.div`
	position: relative;
	overflow: hidden;
	width: 100%;
	height: 100%;
`;

const ContentWrapper = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
	padding: 1rem 0 0 0;
`;

const StyledThumbnailsSwiper = styled(Swiper)`
	display: flex;
	position: relative;
	height: 108px;
	width: 100%;
	margin: 0;

	.swiper-slide {
		height: 90px;
		width: 90px;
		cursor: pointer;
	}

	.swiper-wrapper {
		position: absolute;
	}
`;

const StyledThumbnailImage = styled.img`
	height: 90px;
	width: 90px;
	display: flex;
	object-fit: cover;
	background-color: ${({ theme: { isDarkMode } }) =>
		isDarkMode
			? "var(--dz-ref-color-gray300)"
			: "var(--dz-ref-color-gray100)"};
	${({ $isActive }) =>
		$isActive &&
		css`
			box-sizing: border-box;
			border: 3px solid transparent;
		`}

	transition: transform 100ms;
	transform: scale(1);

	&:hover,
	&:focus {
		transform: scale(1.05);
	}
`;

const StyledActiveThumbnails = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	pointer-events: none;

	${({ $isActive }) =>
		$isActive &&
		css`
			box-sizing: border-box;
			border: 3px solid
				${({ theme: { isDarkMode } }) => (isDarkMode ? "#FFF" : "#000")};
		`}
`;

// #####################################################

const MediaList = ({ setCurrentAvatar, setFirstLoad, list, fetchData }) => {
	// ** Referencja do wrappera - 'contentWrapper'
	const contentWrapperRef = useRef(null);

	// ** Hook useMobileDetect
	const mobileDetect = useMobileDetect();
	// ** Sprawdzamy czy użytkownik korzysta z urzadznie z systemem iOS
	const isIos = mobileDetect.isIos();

	// ** Zmienne stanu komponentu
	const [thumbnailSwiper, setThumbnailSwiper] = useState(null);
	const [showPrevArrow, setShowPrevArrow] = useState(false);
	const [showNextArrow, setShowNextArrow] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);

	const [thumbnailsPerView, setThumbnailsPerView] = useState(5);
	const [centerSlides, setCenterSlides] = useState(false);
	const [isNavArrowClicked, setNavArrowClicked] = useState(false);
	const [isMoved, setMoved] = useState(false);

	// ** Tłumaczenia
	const { t } = useTranslation("userpanel");

	// ** useBreakpoint Hook
	const isPhone = useBreakpoint("phone", "max");

	// ** Funkcja budująca tablice slajdów do wyrenderowania
	const slidesToRender = list.data.map((item, index) => {
		const isActive = activeIndex === index;
		return (
			<SwiperSlide
				className={isActive ? "active" : null}
				key={`avatar-slide-${item.id}`}
				virtualIndex={index}
				onClick={() => {
					setFirstLoad(true);
					setActiveIndex(index);
					thumbnailSwiper.slideTo(index);
					setCurrentAvatar(list.data[index]);
				}}
			>
				<Block flex column style={{ overflow: "hidden" }}>
					{item.media_type === "video" ? (
						<HoverVideoPlayer
							videoSrc={
								<>
									{item.media?.preview_square && (
										<source
											src={item.media?.preview_square}
											type="video/webm"
										/>
									)}
									{item.media?.video && (
										<source
											src={item.media?.video}
											type="video/mp4"
										/>
									)}
								</>
							}
							playbackStartDelay={100}
							playbackRangeEnd={isIos ? 5 : undefined}
							preload="none"
							loop
							unloadVideoOnPaused={true}
							sizingMode="overlay"
							style={{
								cursor: "pointer",
								height: "90px",
								display: "flex",
								objectFit: "cover",
								boxSizing: "border-box",
								overflow: isActive ? "hidden" : "visible",
								border: isActive
									? "3px solid var(--dz-ref-color-white)"
									: "none",
								backgroundColor: `${({
									theme: { isDarkMode },
								}) =>
									isDarkMode
										? "var(--dz-ref-color-gray300)"
										: "var(--dz-ref-color-gray100)"}`,
							}}
							hoverOverlay={<StyledOverlay />}
							pausedOverlay={
								<>
									<StyledOverlay style={{ zIndex: 3 }} />
									<StyledThumbnailImage
										loading="lazy"
										src={item.media?.poster_square}
									/>
									<StyledIcon
										size="modal_thumbnail"
										full
										noBackground
									>
										<PlaySolidIcon />
									</StyledIcon>
								</>
							}
						/>
					) : (
						<>
							<StyledOverlay style={{ zIndex: 3 }} />
							<StyledThumbnailImage
								loading="lazy"
								src={item.media?.square}
								$isActive={isActive}
							/>
						</>
					)}
					<StyledActiveThumbnails $isActive={isActive} />
				</Block>
			</SwiperSlide>
		);
	});

	// ** Efekt w zależności od liczby wyświetlanych slajdów oraz liczby slajdów w całym albumie
	// ** ustawia parametr 'centerSlides' na 'true' lub 'false' i tym samym włacza lub
	// ** wyłacza centrowanie slajdów w slajderze
	useEffect(() => {
		if (thumbnailSwiper?.visibleSlides?.length > 0) {
			if (thumbnailSwiper?.slidesGrid?.length > thumbnailsPerView) {
				setCenterSlides(true);
			} else {
				setCenterSlides(false);
			}
		}
	}, [
		thumbnailSwiper?.visibleSlides?.length,
		thumbnailSwiper?.slidesGrid?.length,
		thumbnailSwiper?.progress,
	]);

	// ##################################

	return list.data.length === 0 ? (
		<Block m={2}>{t("photos.avatar.noPhotos")}</Block>
	) : (
		<Block relative flex column justifyCenter fullHeight>
			<Paragraph mb={0}>{t("photos.avatar.choosePhoto")}:</Paragraph>
			<Wrapper>
				<ContentWrapper ref={contentWrapperRef}>
					<StyledThumbnailsSwiper
						className="thumbnailSwiper noselect"
						modules={[Virtual]}
						onSwiper={(swiper) => {
							setThumbnailSwiper(swiper);
							setThumbnailsPerView(
								(swiper.width + SPACE_BETWEEN) /
									(THUMBNAIL_SIZE + SPACE_BETWEEN)
							);
						}}
						onResize={(swiper) => {
							setThumbnailsPerView(
								(swiper.width + SPACE_BETWEEN) /
									(THUMBNAIL_SIZE + SPACE_BETWEEN)
							);
						}}
						// ** Po osiagnięciu ostatniego slajdu sprawdzamy czy sa jeszcze dostępne
						// ** kolejne slajdy do pobrania, jeżeli tak to pobieramy je i dodajemy do wirtualnej listy
						onReachEnd={() => {
							if (list.currentPage < list.totalPages) {
								fetchData(list.currentPage + 1);
							}
						}}
						slidesPerView={thumbnailsPerView}
						centeredSlides={centerSlides}
						centeredSlidesBounds
						virtual={{
							enabled: true,
							addSlidesAfter: thumbnailsPerView * 2,
							addSlidesBefore: thumbnailsPerView * 2,
							cache: true,
						}}
						lazy={{
							enabled: true,
							checkInView: true,
							loadOnTransitionStart: true,
							loadPrevNext: true,
							loadPrevNextAmount: thumbnailsPerView * 2,
						}}
						watchSlidesProgress
						spaceBetween={SPACE_BETWEEN}
						preventClicks
						preventClicksPropagation
						// ** W zależności od 'swiper.progress' ustawiamy widoczność
						// ** strzałek 'prev' i 'next'
						onSetTranslate={(swiper) => {
							if (
								swiper.progress > 0 &&
								list.data.length > thumbnailsPerView &&
								swiper.activeIndex !== swiper.clickedIndex
							) {
								setShowPrevArrow(true);
							} else {
								setShowPrevArrow(false);
							}
							if (
								swiper.progress < 1 &&
								list.data.length > thumbnailsPerView
							) {
								setShowNextArrow(true);
							} else {
								setShowNextArrow(false);
							}
						}}
						onClick={(swiper) => {
							if (swiper.activeIndex === swiper.clickedIndex) {
								setShowPrevArrow(false);
							}
						}}
						// ** Po zakończeniu 'transition' sprawdzamy czy pozycja slidera zgadza się z pozycją
						// ** zapisaną w 'swiper.slidesGrid' jeżeli nie to przesuwamy slider do odpowiedniej pozycji,
						onTransitionEnd={(swiper) => {
							if (
								!isNavArrowClicked &&
								!isMoved &&
								Math.abs(swiper.getTranslate()) !==
									Math.abs(swiper.slidesGrid[activeIndex])
							) {
								swiper.translateTo(
									swiper.slidesGrid[activeIndex] * -1,
									300,
									false,
									true
								);
							}
						}}
						onSliderFirstMove={() => {
							setMoved(true);
						}}
					>
						{/* Strzałka nawigacji w lewo */}
						{!isPhone && showPrevArrow && (
							<NavigationArrowThumbnails
								direction="prev"
								swiper={thumbnailSwiper}
								thumbnailsPerView={thumbnailsPerView}
								isEditAvatarComponent
								setNavArrowClicked={setNavArrowClicked}
							/>
						)}
						{slidesToRender}
						{/* Strzałka nawigacji w prawo */}
						{!isPhone && showNextArrow && (
							<NavigationArrowThumbnails
								direction="next"
								swiper={thumbnailSwiper}
								thumbnailsPerView={thumbnailsPerView}
								isEditAvatarComponent
								setNavArrowClicked={setNavArrowClicked}
							/>
						)}
					</StyledThumbnailsSwiper>
				</ContentWrapper>
			</Wrapper>
		</Block>
	);
};

export default MediaList;
